import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useReportedMissingSections } from 'src/contexts/ReportedMissingSectionsContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  CatalogReportClient,
  Feature,
  FileParameter,
  VenueMapReportType,
} from 'src/WebApiController';

type ReportVenueMapIssueVars = {
  reportType: VenueMapReportType;
  /** One or more events sharing the venue config (FR4 bulk); single = one entry. */
  eventIds: number[];
  venueConfigId: number | null;
  description: string;
  primaryUrl: string;
  attachments: File[];
};

/**
 * Reports a venue-map issue (incorrect / missing map) to the catalog team via the
 * WorkItem service (POS-9306). One catalog task is created per event, so this
 * serves the single map view, the manual-PO flow, and the bulk multi-event flow.
 */
export const useReportVenueMapIssue = () => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const { markReported } = useReportedMissingSections();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: ReportVenueMapIssueVars) =>
      tryInvokeApi(
        async () => {
          const fileParams: FileParameter[] = vars.attachments.map((f) => ({
            data: f,
            fileName: f.name,
          }));
          return await new CatalogReportClient(
            activeAccountWebClientConfig
          ).reportVenueMapIssueBulk(
            vars.reportType,
            vars.eventIds,
            vars.venueConfigId,
            vars.description || null,
            vars.primaryUrl || null,
            fileParams.length > 0 ? fileParams : null
          );
        },
        (error) => {
          showErrorDialog('CatalogReportClient.reportVenueMapIssueBulk', error);
        }
      ),
    onSuccess: (result, vars) => {
      // tryInvokeApi resolves `undefined` on error — only update on a real report.
      if (!result || result.length === 0) {
        return;
      }
      // A whole-map report covers the venue config: mark it reported so the
      // report-map-issue button and the per-section indicators immediately
      // reflect the pending request (in-session), and refresh the seed so it
      // stays in sync with the backend.
      markReported(vars.venueConfigId, null);
      queryClient.invalidateQueries({
        queryKey: ['CatalogReportClient.getReportedSections'],
      });
    },
  });
};

/**
 * FR6: fetches the prefill proof/scrape URL for a single event's map (the map
 * view / manual-PO flow), keyed by the viagogo event id. Prefill is skipped for
 * the bulk case (many events, one shared URL field). Overridable.
 */
export const useEventProofUrl = (eventIds: number[], enabled: boolean) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const hasFeature = useUserHasFeature(Feature.CatalogSupport);
  const singleEventId = eventIds.length === 1 ? eventIds[0] : null;

  const query = useQuery({
    queryKey: [
      'CatalogReportClient.getEventProofUrls',
      activeAccountWebClientConfig.activeAccountId,
      singleEventId,
    ],
    queryFn: async () => {
      if (singleEventId == null) {
        return null;
      }
      const results = await new CatalogReportClient(
        activeAccountWebClientConfig
      ).getEventProofUrls([singleEventId]);
      return results?.[0]?.url ?? null;
    },
    enabled: hasFeature && enabled && singleEventId != null,
    refetchOnWindowFocus: false,
  });

  return { proofUrl: query.data ?? null };
};
